// src/pages/AdminRestoreWizard.jsx
// Admin-only: upload a .sql / .sql.gz and restore the DB (destructive).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const trim = (s) => (s || "").replace(/\/+$/g, "");
const API = trim(process.env.REACT_APP_API_BASE) || ""; // '' → same origin

function bytesFmt(n) {
  const v = Number(n || 0);
  if (!v) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${x.toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

async function fetchJson(url, opts = {}) {
  const res = await fetch(url, opts);
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : { raw: await res.text() };
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

export default function AdminRestoreWizard() {
  const navigate = useNavigate();

  const token = useMemo(() => localStorage.getItem("token") || "", []);
  const isAdmin = useMemo(() => (localStorage.getItem("is_admin") || "").toLowerCase() === "true", []);

  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [jobId, setJobId] = useState("");
  const [job, setJob] = useState(null);

  const expectedPhrase = "RESTORE FULL DATABASE";

  useEffect(() => {
    if (!isAdmin) navigate("/");
  }, [isAdmin, navigate]);

  // poll job
  useEffect(() => {
    if (!jobId) return;
    let alive = true;

    const tick = async () => {
      try {
        const data = await fetchJson(`${API}/api/admin/restore/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setJob(data.job);
      } catch (e) {
        if (alive) setErr(e?.message || String(e));
      }
    };

    tick();
    const t = setInterval(tick, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [jobId, token]);

  const canStart = !!file && confirm.trim() === expectedPhrase && !busy;

  const startRestore = async () => {
    setErr("");
    if (!file) return setErr("Моля, изберете .sql или .sql.gz файл");
    if (confirm.trim() !== expectedPhrase) return setErr(`Напишете точно: ${expectedPhrase}`);

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("dump", file);
      fd.append("confirm", confirm.trim());

      const data = await fetchJson(`${API}/api/admin/restore`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      setJobId(data.jobId);
      setJob(null);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelRestore = async () => {
    if (!jobId) return;
    setErr("");
    try {
      await fetchJson(`${API}/api/admin/restore/cancel/${jobId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const statusBadge = (s) => {
    const base = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border";
    if (s === "success") return `${base} bg-emerald-50 text-emerald-800 border-emerald-200`;
    if (s === "error") return `${base} bg-red-50 text-red-800 border-red-200`;
    if (s === "running") return `${base} bg-amber-50 text-amber-800 border-amber-200`;
    if (s === "cancelled") return `${base} bg-slate-50 text-slate-800 border-slate-200`;
    return `${base} bg-slate-50 text-slate-700 border-slate-200`;
  };

  return (
    <div className="min-h-screen relative p-6">
      {/* background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/75 via-black/60 to-[#6c56b5]/65" />
      </div>

      <div className="max-w-4xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white">
              🧯 Restore Wizard (Admin)
            </h1>
            <p className="text-sm text-white/80 mt-2 max-w-3xl">
              Това възстановяване е <b>опасно и разрушително</b>. Използвай само когато POS е спрян и имаш валиден dump.
              Най-добре: направи backup преди restore.
            </p>
          </div>

          <button
            onClick={() => navigate("/admin/dashboard")}
            className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/15"
          >
            ← Назад
          </button>
        </div>

        {/* warning card */}
        <div className="mt-5 p-4 rounded-xl bg-red-500/10 border border-red-300/30 text-white">
          <div className="font-bold">⚠️ Внимание</div>
          <ul className="text-sm text-white/85 mt-2 list-disc pl-5 space-y-1">
            <li>Restore може да презапише таблици/данни и да счупи системата при грешен файл.</li>
            <li>Поддържани файлове: <b>.sql</b> или <b>.sql.gz</b> (mysqldump).</li>
            <li>Потвърждението е задължително: <b>{expectedPhrase}</b></li>
          </ul>
        </div>

        {/* form */}
        <div className="mt-6 grid gap-4">
          <div className="grid gap-2">
            <label className="text-sm font-semibold text-white">SQL dump файл</label>
            <input
              type="file"
              accept=".sql,.gz,.sql.gz"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-white/15 file:text-white hover:file:bg-white/20"
            />
            {file && (
              <div className="text-xs text-white/80">
                Избран файл: <b>{file.name}</b> · {bytesFmt(file.size)}
              </div>
            )}
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-semibold text-white">Потвърди (точно)</label>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={expectedPhrase}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400/70"
            />
            <div className="text-xs text-white/70">
              За да започнеш restore, напиши <b>{expectedPhrase}</b>.
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              disabled={!canStart}
              onClick={startRestore}
              className={`px-5 py-2 rounded-lg font-semibold text-white border border-white/15 shadow-lg transition
                ${canStart ? "bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800" : "bg-white/10 opacity-60 cursor-not-allowed"}`}
            >
              {busy ? "Стартиране..." : "▶️ Start restore"}
            </button>

            <button
              disabled={!jobId || job?.status !== "running"}
              onClick={cancelRestore}
              className={`px-5 py-2 rounded-lg font-semibold text-white border border-white/15 transition
                ${jobId && job?.status === "running" ? "bg-red-600/80 hover:bg-red-600" : "bg-white/10 opacity-60 cursor-not-allowed"}`}
            >
              ⛔ Cancel
            </button>
          </div>

          {err && (
            <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
              {err}
            </div>
          )}
        </div>

        {/* job status */}
        <div className="mt-8">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-white">Статус</h2>
            {jobId && <span className="text-xs text-white/70">Job ID: {jobId}</span>}
            {job?.status && <span className={statusBadge(job.status)}>{job.status}</span>}
          </div>

          {!jobId ? (
            <p className="text-sm text-white/70 mt-2">Няма стартиран restore.</p>
          ) : (
            <div className="mt-3 grid gap-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-4 rounded-xl bg-white/8 border border-white/10 text-white">
                  <div className="text-sm font-semibold">Файл</div>
                  <div className="text-sm text-white/85 mt-1">
                    {job?.originalName || "—"}
                  </div>
                  <div className="text-xs text-white/70 mt-1">
                    {job ? `${bytesFmt(job.bytes)} · ${job.gzip ? "gzip" : "plain"}` : ""}
                  </div>
                  {job?.sha256 && (
                    <div className="text-xs text-white/60 mt-2 break-all">
                      sha256: {job.sha256}
                    </div>
                  )}
                </div>

                <div className="p-4 rounded-xl bg-white/8 border border-white/10 text-white">
                  <div className="text-sm font-semibold">Време</div>
                  <div className="text-xs text-white/70 mt-2 space-y-1">
                    <div>created: {job?.createdAt || "—"}</div>
                    <div>started: {job?.startedAt || "—"}</div>
                    <div>finished: {job?.finishedAt || "—"}</div>
                    <div>exit: {job?.exitCode ?? "—"}</div>
                    {job?.error && <div className="text-red-200">error: {job.error}</div>}
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-black/30 border border-white/10">
                <div className="text-sm font-semibold text-white mb-2">Logs (последни редове)</div>
                <pre className="text-xs text-white/80 whitespace-pre-wrap max-h-[320px] overflow-auto">
                  {(job?.logs || []).join("\n") || "(no logs)"}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-white/60">
          Tip: ако dump съдържа CREATE/DROP TABLE, ще възстанови schema + data. Ако е само INSERT-и, ще допълни текущата база.
        </div>
      </div>
    </div>
  );
}
